import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { IntlProvider, useIntl, type PrimitiveType } from "react-intl";

import { englishMessages, type MessageId } from "@/i18n/catalogs/en";
import { createLocaleFormatters, type LocaleFormatters } from "@/i18n/formatters";
import {
  DEFAULT_LOCALE,
  LOCALE_STORAGE_KEY,
  detectLocale,
  isSupportedLocale,
  loadCatalog,
  type MessageCatalog,
  type SupportedLocale,
} from "@/i18n/locales";
import { setRuntimeCatalog } from "@/i18n/runtime";

interface I18nContextValue {
  locale: SupportedLocale;
  setLocale: (locale: SupportedLocale) => void;
  isLoading: boolean;
}

const I18nContext = createContext<I18nContextValue | undefined>(undefined);

export interface MissingTranslationDetail {
  id: string;
  locale: string;
}

function reportMissingTranslation(id: string, locale: string): void {
  window.dispatchEvent(
    new CustomEvent<MissingTranslationDetail>("celsus:i18n-missing", {
      detail: { id, locale },
    })
  );
  if (import.meta.env.DEV) console.warn(`[i18n] Missing message "${id}" for ${locale}`);
}

export function I18nProvider({ children }: { children: ReactNode }) {
  const [locale, setLocaleState] = useState<SupportedLocale>(() => detectLocale());
  const [messages, setMessages] = useState<MessageCatalog>(englishMessages);
  const [isLoading, setIsLoading] = useState(false);

  const setLocale = useCallback((nextLocale: SupportedLocale): void => {
    window.localStorage.setItem(LOCALE_STORAGE_KEY, nextLocale);
    setLocaleState(nextLocale);
  }, []);

  useEffect(() => {
    let current = true;
    document.documentElement.lang = locale;
    window.localStorage.setItem(LOCALE_STORAGE_KEY, locale);
    setIsLoading(true);
    void loadCatalog(locale).then((catalog) => {
      if (current) {
        setMessages(catalog);
        setRuntimeCatalog(locale, catalog);
        setIsLoading(false);
      }
    });
    return () => {
      current = false;
    };
  }, [locale]);

  const value = useMemo(
    () => ({ locale, setLocale, isLoading }),
    [locale, setLocale, isLoading]
  );
  return (
    <I18nContext.Provider value={value}>
      <IntlProvider
        defaultLocale={DEFAULT_LOCALE}
        locale={locale}
        messages={messages}
        onError={(error) => {
          if (error.code === "MISSING_TRANSLATION") {
            reportMissingTranslation(String(error.descriptor?.id ?? "unknown"), locale);
            return;
          }
          if (import.meta.env.DEV) console.error(error);
        }}
      >
        {children}
      </IntlProvider>
    </I18nContext.Provider>
  );
}

export function useI18n(): I18nContextValue {
  const context = useContext(I18nContext);
  if (!context) throw new Error("useI18n must be used within I18nProvider");
  return context;
}

export function useTranslations(): (
  id: MessageId,
  values?: Record<string, PrimitiveType>
) => string {
  const intl = useIntl();
  return useCallback(
    (id, values) =>
      intl.formatMessage({ id, defaultMessage: englishMessages[id] }, values),
    [intl]
  );
}

export function useLocaleFormatters(): LocaleFormatters {
  const context = useContext(I18nContext);
  const locale = context?.locale ?? DEFAULT_LOCALE;
  return useMemo(() => createLocaleFormatters(locale), [locale]);
}

export function normalizeSupportedLocale(value: string): SupportedLocale {
  return isSupportedLocale(value) ? value : DEFAULT_LOCALE;
}
